import { useState } from 'react';

import { Box, Button, Divider, Flex, Heading, SimpleGrid, Tag, Text, VisuallyHidden } from '@chakra-ui/react';

import { ExternalLink } from '~/components/external-link';
import { SanityImage } from '~/components/sanity-image';
import { format, useFormatDate } from '~/hooks/use-format-date';

import { NewsItem, NewsList } from '../models/news';

export function NewsArticlesSection({ articles }: { articles: NewsList }) {
  const [numberOfArticlesToRender, setNumberOfArticlesToRender] = useState(6);

  return (
    <Box as='section'>
      <VisuallyHidden>
        <Heading as='h2'>News Articles</Heading>
      </VisuallyHidden>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: '24px', md: '32px' }}>
        {articles.slice(0, numberOfArticlesToRender).map((article) => (
          <NewsArticleCard key={article._id} article={article} />
        ))}
      </SimpleGrid>
      {numberOfArticlesToRender < articles.length && (
        <Flex justifyContent='center'>
          <Button marginTop='56px' variant='outline' onClick={() => setNumberOfArticlesToRender((prev) => prev + 6)}>
            Load More News
          </Button>
        </Flex>
      )}
    </Box>
  );
}

export function NewsArticleCard({ article }: { article: NewsItem }) {
  const publishDate = useFormatDate({ date: new Date(article.publishDate), format: format.monthDayYear });

  return (
    <Box
      as='article'
      bg='white'
      borderBottomLeftRadius='24px'
      borderBottomRightRadius='base'
      borderTopLeftRadius='base'
      borderTopRightRadius='24px'
      overflow='hidden'
      shadow='lg'
    >
      {article.image && (
        <Box height='180px' position='relative'>
          <SanityImage image={article.image} style={{ objectFit: 'cover', height: '100%', width: '100%' }} priority />
        </Box>
      )}
      <Flex bg='white' direction='column' gap='16px' padding='24px 24px 32px 24px'>
        <div>
          <Tag marginRight='16px'>{publishDate}</Tag>

          <Text as='span' size='label'>
            {article.publication}
          </Text>
        </div>
        <Divider />
        <Heading as='h3' size='h4'>
          {article.title}
        </Heading>
        <ExternalLink href={article.newsLink} withArrow>
          Read more
        </ExternalLink>
      </Flex>
    </Box>
  );
}
